import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// The builder's project setup guide (Kloepfer K8-9; Phelipe 2026-10-09): the
// "Set up your project · 1/5" label in the top bar (#setupGuidePill, on every
// page of the job) opens the modal with the five steps. The first visit after
// Create gets a one-time tooltip under the label, gone on the first click
// anywhere. Hide this guide puts label and modal away for this project, with
// no page reload. The label sits in the header, outside this element, so the
// controller listens for its clicks at the document.
export default class extends Controller {
  static targets = ["modal", "tip", "tipArrow"]
  static values = { url: String, projectId: Number, done: Number, tip: Boolean }

  connect() {
    this._onDocClick = this._onDocClick.bind(this)
    this._onKeydown = this._onKeydown.bind(this)
    document.addEventListener("click", this._onDocClick)
    document.addEventListener("keydown", this._onKeydown)
    if (this.tipValue) this._showTip()
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick)
    document.removeEventListener("keydown", this._onKeydown)
    document.body.style.overflow = ""
  }

  open() {
    this._hideTip()
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    track("Project Setup Guide Opened", { project_id: this.projectIdValue, steps_done: this.doneValue })
  }

  close() {
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // A click anywhere on a step's row goes to that step (Phelipe 2026-10-09).
  // The row's own links and buttons keep their job: the Go link, the help
  // article. Going = clicking the row's Go link, so the analytics ride along.
  rowGo(event) {
    if (event.target.closest("a, button")) return
    event.currentTarget.querySelector("a[data-step-link]")?.click()
  }

  rowKey(event) {
    if (event.target !== event.currentTarget) return
    if (event.key !== "Enter" && event.key !== " ") return
    event.preventDefault()
    event.currentTarget.querySelector("a[data-step-link]")?.click()
  }

  async hide() {
    this.close()
    document.getElementById("setupGuidePill")?.remove()
    try {
      await fetch(this.urlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "", "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ steps_done: this.doneValue }),
        credentials: "same-origin"
      })
    } catch (_) { /* hidden on screen either way; the next visit shows it again */ }
    track("Project Setup Guide Hidden", { project_id: this.projectIdValue, steps_done: this.doneValue })
    this.element.remove()
    // The "Show the setup guide" box on the Construction page unticks itself.
    window.dispatchEvent(new CustomEvent("setup-guide:hidden"))
  }

  _onDocClick(event) {
    if (event.target.closest("#setupGuidePill")) {
      event.preventDefault()
      this.open()
    } else {
      this._hideTip()
    }
  }

  _onKeydown(event) {
    if (event.key !== "Escape") return
    this._hideTip()
    if (!this.modalTarget.classList.contains("hidden")) this.close()
  }

  // Once per browser for this project: a reload of the same page, or a second
  // project created later, doesn't nag.
  _showTip() {
    const key = `setup-guide-tip:${this.projectIdValue}`
    try {
      if (localStorage.getItem(key)) return
      localStorage.setItem(key, "1")
    } catch (_) { /* private window: show it this once */ }
    const pill = document.getElementById("setupGuidePill")
    if (!pill || !this.hasTipTarget) return
    const tip = this.tipTarget
    tip.classList.remove("hidden")
    const rect = pill.getBoundingClientRect()
    const width = tip.offsetWidth
    let x = rect.left + rect.width / 2 - width / 2
    x = Math.max(8, Math.min(x, window.innerWidth - width - 8))
    tip.style.left = `${Math.round(x)}px`
    tip.style.top = `${Math.round(rect.bottom + 10)}px`
    // The arrow points at the label's middle even when the bubble was pushed sideways.
    if (this.hasTipArrowTarget) this.tipArrowTarget.style.left = `${Math.round(rect.left + rect.width / 2 - x - 4)}px`
    track("Project Setup Guide Tip Shown", { project_id: this.projectIdValue })
  }

  _hideTip() {
    if (this.hasTipTarget) this.tipTarget.classList.add("hidden")
  }
}
